import axios from 'axios';
import type {
  LoginRequest,
  LoginResponse,
  Batch,
  CreateBatchRequest,
  User,
  CreateUserRequest,
  SMS,
  NumberStatus,
  GlobalSettings,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle unauthorized responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication
export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', data);
    return response.data;
  },

  refreshToken: async (): Promise<{ token: string; expiresAt: string }> => {
    const response = await api.post('/auth/refresh');
    return response.data;
  },
};

// Admin - Batches (read-only for regular admins)
export const batchApi = {
  // Read operations (available to all admins)
  list: async (): Promise<Batch[]> => {
    const response = await api.get<Batch[]>('/admin/batches');
    return response.data;
  },

  getById: async (id: string): Promise<Batch> => {
    const response = await api.get<Batch>(`/admin/batches/${id}`);
    return response.data;
  },

  // Write operations (SuperAdmin only - routes under /superadmin)
  create: async (data: CreateBatchRequest): Promise<Batch> => {
    const response = await api.post<Batch>('/superadmin/batches', data);
    return response.data;
  },

  update: async (id: string, data: Partial<CreateBatchRequest>): Promise<void> => {
    await api.put(`/superadmin/batches/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/superadmin/batches/${id}`);
  },

  toggleEnabled: async (id: string, enabled: boolean): Promise<{ message: string; enabled: boolean }> => {
    const response = await api.patch(`/superadmin/batches/${id}/toggle`, { enabled });
    return response.data;
  },
};

// Admin - Users
export const userApi = {
  create: async (data: CreateUserRequest): Promise<User> => {
    const response = await api.post<User>('/admin/users', data);
    return response.data;
  },

  list: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/admin/users');
    return response.data;
  },

  getById: async (id: string): Promise<User> => {
    const response = await api.get<User>(`/admin/users/${id}`);
    return response.data;
  },

  update: async (id: string, data: Partial<CreateUserRequest>): Promise<void> => {
    await api.put(`/admin/users/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/users/${id}`);
  },

  getUserBatches: async (id: string): Promise<Batch[]> => {
    const response = await api.get<Batch[]>(`/admin/users/${id}/batches`);
    return response.data;
  },

  assignBatch: async (userId: string, batchId: string): Promise<void> => {
    await api.post(`/admin/users/${userId}/assign-batch`, { batchId });
  },

  removeBatch: async (userId: string, batchId: string): Promise<void> => {
    await api.delete(`/admin/users/${userId}/remove-batch`, { data: { batchId } });
  },
};

// User - Numbers
export const numberApi = {
  getNumbers: async (): Promise<NumberStatus[]> => {
    const response = await api.get<NumberStatus[]>('/numbers');
    return response.data;
  },

  getByCountry: async (): Promise<Record<string, NumberStatus[]>> => {
    const response = await api.get<Record<string, NumberStatus[]>>('/numbers/by-country');
    return response.data;
  },

  getByBatch: async (): Promise<Record<string, any>> => {
    const response = await api.get<Record<string, any>>('/numbers/by-batch');
    return response.data;
  },
};

// User - SMS
export const smsApi = {
  getSMS: async (params?: {
    phoneNumber?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{ messages: SMS[]; total: number; page: number; limit: number }> => {
    const response = await api.get('/sms', { params });
    return response.data;
  },

  search: async (phoneNumber: string): Promise<SMS[]> => {
    const response = await api.get<SMS[]>('/sms/search', {
      params: { phoneNumber },
    });
    return response.data;
  },

  getByBatch: async (batchId: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<{ messages: SMS[]; total: number; page: number; limit: number }> => {
    const response = await api.get(`/sms/batch/${batchId}`, { params });
    return response.data;
  },
};

// Admin - Analytics
export const analyticsApi = {
  getMessagesByDay: async (params?: {
    days?: number;
    batch_id?: string;
    country?: string;
  }): Promise<{ data: Array<{ date: string; count: number }>; start_date: string; end_date: string }> => {
    const response = await api.get('/admin/analytics/messages-by-day', { params });
    return response.data;
  },

  getMessagesByHour: async (params?: {
    days?: number;
    batch_id?: string;
    country?: string;
  }): Promise<{ data: Array<{ hour: number; count: number }>; start_date: string; end_date: string }> => {
    const response = await api.get('/admin/analytics/messages-by-hour', { params });
    return response.data;
  },

  getMessagesByBatch: async (params?: {
    days?: number;
    country?: string;
  }): Promise<{ data: Array<{ batch_id: string; batch_name: string; count: number }>; start_date: string; end_date: string }> => {
    const response = await api.get('/admin/analytics/messages-by-batch', { params });
    return response.data;
  },

  getMessagesByCountry: async (params?: {
    days?: number;
    batch_id?: string;
  }): Promise<{ data: Array<{ country: string; count: number }>; start_date: string; end_date: string }> => {
    const response = await api.get('/admin/analytics/messages-by-country', { params });
    return response.data;
  },

  getSummaryStats: async (): Promise<{
    total_messages_7d: number;
    messages_today: number;
    top_batch: { batch_id: string; count: number };
    top_country: { country: string; count: number };
    period: string;
  }> => {
    const response = await api.get('/admin/analytics/summary');
    return response.data;
  },
};

// SuperAdmin - Global Settings
export const globalSettingsApi = {
  getFilterRuleOptions: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/filter-rule-options');
    return response.data;
  },

  getSetting: async (key: string): Promise<GlobalSettings> => {
    const response = await api.get<GlobalSettings>(`/superadmin/global-settings/${key}`);
    return response.data;
  },

  updateSetting: async (key: string, data: { options: string[] }): Promise<{ message: string }> => {
    const response = await api.put(`/superadmin/global-settings/${key}`, data);
    return response.data;
  },
};

// Extended SMS type with filter status
export interface SMSWithFilterStatus extends SMS {
  filterPassed: boolean;
  matchedFilter?: string;
}

// SuperAdmin - Batch Messages and Sync
export const superAdminApi = {
  getBatchMessages: async (batchId: string, limit?: number): Promise<{
    messages: SMSWithFilterStatus[];
    total: number;
    batch_name: string;
    last_sync: string | null;
    filter_options: string[];
  }> => {
    const response = await api.get(`/superadmin/batches/${batchId}/messages`, {
      params: { limit: limit || 50 },
    });
    return response.data;
  },

  syncBatch: async (batchId: string, lookbackMinutes?: number): Promise<{
    batch_name: string;
    total_fetched: number;
    new_messages: number;
    errors: number;
    synced_at: string;
  }> => {
    const response = await api.post(`/superadmin/batches/${batchId}/sync`, null, {
      params: { lookback: lookbackMinutes || 60 },
    });
    return response.data;
  },
};

export default api;
